import { isValidElement } from 'react'

import styled from '@emotion/styled'
import type { ReactElement, ReactNode } from 'react'

import type { ArcadeIcon } from '@arcade/web/icons/react'
import { PushIcon } from '@arcade/web/icons/react'
import type { BaseCellAsset } from '@cash-design-system/react'
import { BaseCell, CellButtonWrapper, Text } from '@cash-design-system/react'

const CellWrapper = CellButtonWrapper.withComponent('div')

const StyledCellWrapper = styled(CellWrapper)<{ disabled: boolean; isClickable: boolean }>`
  background-color: unset;
  color: ${props => props.theme.colors.label};
  margin: 0;
  height: 100%;
  align-content: center;

  &:hover {
    border-radius: 0;
  }

  ${({ isClickable }) =>
    !isClickable &&
    `
    pointer-events: none;
    cursor: not-allowed;
  `}
`

interface SubtitleProps {
  truncate?: boolean
}

const Subtitle = styled(Text)<SubtitleProps>`
  /* restrict our card items to 2 lines and ellipse */
  ${props =>
    props.truncate &&
    `
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  `}
`

export interface CardItemProps {
  title: string
  subtitle?: string | ReactNode
  icon?: ArcadeIcon | ReactElement
  isClickable?: boolean
  truncate?: boolean
  disabled?: boolean
}

const getAssetForIcon = (icon?: ArcadeIcon | ReactElement): BaseCellAsset | undefined => {
  if (!icon) return undefined
  if (isValidElement(icon)) {
    return { avatar: icon }
  } else {
    // It must be an ArcadeIcon if not a ReactElement
    return { Icon: icon as ArcadeIcon }
  }
}

export default function CardItem({
  title,
  subtitle,
  icon,
  isClickable = false,
  truncate = true,
  disabled = false,
}: CardItemProps) {
  const formattedSubtitle = subtitle ? (
    <Subtitle color="textSubtle" truncate={truncate}>
      {subtitle}
    </Subtitle>
  ) : undefined

  const accessory = isClickable ? <PushIcon variant="standard" data-testid="chevron-right" /> : undefined

  return (
    <StyledCellWrapper disabled={disabled} isClickable={isClickable}>
      <BaseCell
        label={title}
        body={formattedSubtitle}
        asset={getAssetForIcon(icon)}
        accessory={accessory}
        disabled={disabled}
      />
    </StyledCellWrapper>
  )
}
